import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient, useOptionalResourceChanges } from "@authe/ui-core/api";
import { useSession } from "@authe/ui-core/session";
import { connectResourceChanges, streamChannelIdsForCaller, subscribeDescriptor, useOptionalStreamingClient } from "@authe/streaming";
import { connectResourceCaches, resourceCacheBindings } from "@/api/resourceSync";
import { queryDependsOnResources } from "@/api/queryResourcePolicies";
import { watchCatalogChangesForOverlayDisk } from "@/features/overlays/addressOverlayDiskCache";
import { useTeamIds } from "./useTeamRoster";

/** One cache/stream bridge per authenticated dashboard, independent of routes. */
export function DashboardResourceSync(): null {
  const hub = useOptionalResourceChanges();
  const api = useApiClient();
  const queries = useQueryClient();
  const client = useOptionalStreamingClient();
  const { session, manager } = useSession();
  const teamIds = useTeamIds();
  const teamsKey = JSON.stringify([...teamIds].sort());
  const customerId = session?.tenant.customerId;
  const orgId = session?.tenant.orgId;
  const tenantId = session?.tenant.tenantId;
  const userId = session?.user.id;

  React.useEffect(() => {
    if (!hub) return;
    const disconnect = connectResourceCaches(hub, api, queries);
    // Kept catalog answers on disk stop being trusted on any change the
    // in-memory `address-overlay-ref` entries would be invalidated for.
    const offDisk = watchCatalogChangesForOverlayDisk(hub, (resource) =>
      queryDependsOnResources(["address-overlay-ref"], new Set([resource])));
    // Clear caches synchronously at identity changes, before the next render.
    const offIdentity = manager.subscribeIdentityChange(disconnect);
    return () => { offIdentity(); offDisk(); disconnect(); };
  }, [hub, api, queries, manager]);

  React.useEffect(() => {
    if (!hub || !client) return;
    return connectResourceChanges(client, hub, resourceCacheBindings.flatMap(({ resource, stream }) => stream ? [{
      resource, stream,
      channels: streamChannelIdsForCaller({ customerId, orgId, tenantId, userId }, stream, JSON.parse(teamsKey) as string[]).map(subscribeDescriptor),
    }] : []));
  }, [hub, client, customerId, orgId, tenantId, userId, teamsKey]);
  return null;
}
